import { useState } from "react";
import {
  listPlugins,
  registerPlugin,
  enablePlugin,
  disablePlugin,
  uninstallPlugin,
} from "../api/enterprise.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

type Plugin = {
  id: string;
  name: string;
  displayName: string;
  version: string;
  description: string | null;
  status: string;
  permissions: string[];
  createdAt: string;
};

export function AdminPluginsPage() {
  const { token } = useAuth();
  const [orgId, setOrgId] = useState("");
  const [plugins, setPlugins] = useState<Plugin[]>([]);
  const [form, setForm] = useState({
    name: "",
    displayName: "",
    version: "1.0.0",
    description: "",
    createdBy: "",
  });

  async function load() {
    if (!orgId) return;
    const res = (await listPlugins(token, orgId)) as Plugin[];
    setPlugins(res ?? []);
  }

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault();
    await registerPlugin(token, { orgId, ...form });
    await load();
  }

  async function handleEnable(id: string) {
    await enablePlugin(token, id);
    await load();
  }
  async function handleDisable(id: string) {
    await disablePlugin(token, id);
    await load();
  }
  async function handleUninstall(id: string) {
    if (!window.confirm("Uninstall this plugin?")) return;
    await uninstallPlugin(token, id);
    await load();
  }

  return (
    <Layout>
      <div className="space-y-6 p-6">
        <h1 className="text-2xl font-bold text-white">Plugins &amp; Extensions</h1>

        <div className="space-y-4 rounded-lg border border-gray-800 bg-gray-900 p-5">
          <div className="flex gap-3">
            <input
              className="flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
              placeholder="Organization ID"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
            />
            <button
              onClick={load}
              className="rounded bg-gray-700 px-4 py-2 text-white hover:bg-gray-600"
            >
              Load
            </button>
          </div>

          <form onSubmit={handleRegister} className="grid grid-cols-2 gap-3">
            <input
              className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
              placeholder="Plugin name (slug)"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              required
            />
            <input
              className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
              placeholder="Display name"
              value={form.displayName}
              onChange={(e) => setForm((p) => ({ ...p, displayName: e.target.value }))}
              required
            />
            <input
              className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
              placeholder="Version (e.g. 1.0.0)"
              value={form.version}
              onChange={(e) => setForm((p) => ({ ...p, version: e.target.value }))}
            />
            <input
              className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
              placeholder="Created by user ID"
              value={form.createdBy}
              onChange={(e) => setForm((p) => ({ ...p, createdBy: e.target.value }))}
              required
            />
            <input
              className="col-span-2 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
              placeholder="Description (optional)"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
            <button
              type="submit"
              className="col-span-2 rounded bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
            >
              Register Plugin
            </button>
          </form>
        </div>

        {plugins.length > 0 && (
          <div className="rounded-lg border border-gray-800 bg-gray-900 p-5">
            <div className="space-y-3">
              {plugins.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between border-b border-gray-800 pb-3 last:border-0 last:pb-0"
                >
                  <div>
                    <div className="font-medium text-white">
                      {p.displayName} <span className="text-xs text-gray-500">v{p.version}</span>
                    </div>
                    {p.description && <div className="text-sm text-gray-400">{p.description}</div>}
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`rounded px-2 py-0.5 text-xs ${p.status === "ENABLED" ? "bg-green-900 text-green-200" : "bg-gray-800 text-gray-400"}`}
                    >
                      {p.status}
                    </span>
                    {p.status === "ENABLED" ? (
                      <button
                        onClick={() => handleDisable(p.id)}
                        className="text-xs text-yellow-400 hover:underline"
                      >
                        Disable
                      </button>
                    ) : (
                      <button
                        onClick={() => handleEnable(p.id)}
                        className="text-xs text-green-400 hover:underline"
                      >
                        Enable
                      </button>
                    )}
                    <button
                      onClick={() => handleUninstall(p.id)}
                      className="text-xs text-red-400 hover:underline"
                    >
                      Uninstall
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
